import { useEffect, useRef } from "react";
import { normalizeKey } from "../../../shared-utils/utils";
import { createLockIcon } from "../utils/createLockIcon";
export const useSvgAreaHandlers = ({ svgText, containerRef, sectionImages, areaNames, minLadiesRequired, femaleCount, lowestPriceAreaName, onAreaClick, onInsufficientLadiesClick, }) => {
    const validIdsRef = useRef(new Set());
    const enabledIdsRef = useRef(new Set());
    // Setup click handlers and apply styles
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !svgText)
            return;
        const svgElement = container.querySelector("svg");
        if (!svgElement)
            return;
        // Compute valid and enabled IDs directly in this effect
        const validIds = new Set(sectionImages.map((id) => normalizeKey(id)).filter(Boolean));
        const enabledIds = new Set(areaNames.map((id) => normalizeKey(id)).filter(Boolean));
        // Update refs for potential use elsewhere
        validIdsRef.current = validIds;
        enabledIdsRef.current = enabledIds;
        const getClickableParent = (el) => {
            let current = el;
            while (current && current !== svgElement) {
                if (current.tagName.toLowerCase() === "g" && current.id) {
                    // Skip text elements (IDs ending with _TEXT)
                    if (current.id.toUpperCase().endsWith("_TEXT")) {
                        current = current.parentElement;
                        continue;
                    }
                    const normalizedId = normalizeKey(current.id);
                    // Check if ID matches directly (old format: SUNDECK)
                    if (validIds.has(normalizedId)) {
                        return current;
                    }
                    // Check if base area name matches (new format: SUNDECK_REGULAR)
                    // Extract the part before underscore and check if it's in validIds
                    if (current.id.includes("_")) {
                        const baseAreaName = current.id.split("_")[0];
                        const normalizedBaseArea = normalizeKey(baseAreaName);
                        if (validIds.has(normalizedBaseArea)) {
                            return current;
                        }
                    }
                }
                current = current.parentElement;
            }
            return null;
        };
        const handleClick = (e) => {
            var _a;
            const parent = getClickableParent(e.target);
            if (!parent)
                return;
            // Check if this area is enabled
            // Support both old format (SUNDECK) and new format (SUNDECK_REGULAR)
            const normalizedId = normalizeKey(parent.id);
            // Also check without underscore for old format compatibility
            const idWithoutUnderscore = parent.id.replace(/_/g, "");
            const normalizedIdWithoutUnderscore = normalizeKey(idWithoutUnderscore);
            const isEnabled = enabledIds.has(normalizedId) ||
                enabledIds.has(normalizedIdWithoutUnderscore);
            if (!isEnabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            // Check if this area has insufficient ladies
            // For new format (SUNDECK_REGULAR), check minLadies with full ID first (SUNDECK_REGULAR)
            // If not found, fallback to base area (SUNDECK)
            // For old format (SUNDECK), check with full name
            const baseAreaName = parent.id.includes("_")
                ? parent.id.split("_")[0]
                : parent.id;
            const fullIdUpperCase = parent.id.trim().toUpperCase();
            const baseAreaUpperCase = baseAreaName.trim().toUpperCase();
            // Try full ID first, then fallback to base area name
            const minRequired = (_a = minLadiesRequired === null || minLadiesRequired === void 0 ? void 0 : minLadiesRequired[fullIdUpperCase]) !== null && _a !== void 0 ? _a : minLadiesRequired === null || minLadiesRequired === void 0 ? void 0 : minLadiesRequired[baseAreaUpperCase];
            const currentFemaleCount = femaleCount !== null && femaleCount !== void 0 ? femaleCount : 0;
            if (minRequired &&
                currentFemaleCount < minRequired &&
                onInsufficientLadiesClick) {
                onInsufficientLadiesClick(parent.id.trim(), minRequired);
                return;
            }
            svgElement
                .querySelectorAll('g[data-selected="true"]')
                .forEach((el) => el.removeAttribute("data-selected"));
            parent.setAttribute("data-selected", "true");
            onAreaClick === null || onAreaClick === void 0 ? void 0 : onAreaClick(parent.id);
        };
        // Apply styles to all groups and clear previous recommendation styles
        svgElement.querySelectorAll("g").forEach((g) => {
            g.style.cursor = "";
            g.style.opacity = "";
            // Clear recommendation glow from all paths
            const paths = g.querySelectorAll("path, rect, polygon, circle, ellipse");
            paths.forEach((path) => {
                path.style.filter = "";
                path.style.strokeWidth = "";
            });
        });
        svgElement.querySelectorAll("g[id]").forEach((g) => {
            var _a;
            const normalizedId = normalizeKey(g.id);
            // Extract base area name for both old and new formats
            const baseAreaName = g.id.includes("_") ? g.id.split("_")[0] : g.id;
            const normalizedBaseArea = normalizeKey(baseAreaName);
            // Check if this is a valid clickable area
            if (!normalizedId ||
                (!validIds.has(normalizedId) && !validIds.has(normalizedBaseArea))) {
                return;
            }
            const fullIdUpperCase = g.id.trim().toUpperCase();
            const baseAreaUpperCase = baseAreaName.trim().toUpperCase();
            const minRequired = (_a = minLadiesRequired === null || minLadiesRequired === void 0 ? void 0 : minLadiesRequired[fullIdUpperCase]) !== null && _a !== void 0 ? _a : minLadiesRequired === null || minLadiesRequired === void 0 ? void 0 : minLadiesRequired[baseAreaUpperCase];
            const currentFemaleCount = femaleCount !== null && femaleCount !== void 0 ? femaleCount : 0;
            const isLocked = minRequired && currentFemaleCount < minRequired;
            // First check with areaName_tableType, then fallback to areaName only
            const isLowestPriceArea = !isLocked &&
                lowestPriceAreaName &&
                lowestPriceAreaName.areaName &&
                (() => {
                    const { areaName: recAreaName, tableType: recTableType } = lowestPriceAreaName;
                    const combinedRecommendation = `${recAreaName}_${recTableType}`;
                    if (normalizeKey(g.id) === normalizeKey(combinedRecommendation)) {
                        return true;
                    }
                    if (!g.id.includes("_") &&
                        normalizedBaseArea === normalizeKey(recAreaName)) {
                        return true;
                    }
                    return false;
                })();
            const idWithoutUnderscore = g.id.replace(/_/g, "");
            const normalizedIdWithoutUnderscore = normalizeKey(idWithoutUnderscore);
            const isEnabled = enabledIds.has(normalizedId) ||
                enabledIds.has(normalizedIdWithoutUnderscore);
            if (isEnabled) {
                g.style.cursor = "pointer";
                g.style.opacity = "1";
                if (isLowestPriceArea) {
                    const paths = g.querySelectorAll("path, rect, polygon, circle, ellipse");
                    paths.forEach((path) => {
                        // Get the original stroke color from the element
                        const strokeColor = path.getAttribute("stroke") ||
                            window.getComputedStyle(path).stroke ||
                            "#8155FE"; // fallback to brand purple
                        // Convert stroke color to RGB for glow effect
                        let r = 129, g = 85, b = 254; // default purple RGB
                        // Try to parse the color
                        if (strokeColor.startsWith("#")) {
                            const hex = strokeColor.replace("#", "");
                            if (hex.length === 6) {
                                r = parseInt(hex.substr(0, 2), 16);
                                g = parseInt(hex.substr(2, 2), 16);
                                b = parseInt(hex.substr(4, 2), 16);
                            }
                        }
                        else if (strokeColor.startsWith("rgb")) {
                            const match = strokeColor.match(/\d+/g);
                            if (match && match.length >= 3) {
                                r = parseInt(match[0]);
                                g = parseInt(match[1]);
                                b = parseInt(match[2]);
                            }
                        }
                        // Apply multiple layered glow effects for better visibility
                        const glowColor = `rgba(${r}, ${g}, ${b}, 0.6)`;
                        const strongGlowColor = `rgba(${r}, ${g}, ${b}, 0.8)`;
                        path.style.filter = `
              drop-shadow(0 0 4px ${glowColor})
              drop-shadow(0 0 8px ${glowColor})
              drop-shadow(0 0 12px ${strongGlowColor})
            `.trim();
                        // Enhance the stroke slightly
                        path.style.strokeWidth = "2.5";
                    });
                }
                // Remove existing warning icon if any
                const existingWarning = g.querySelector(".insufficient-ladies-warning");
                if (existingWarning) {
                    existingWarning.remove();
                }
                if (isLocked) {
                    const bbox = g.getBBox();
                    const areaNameForText = g.id.trim().toUpperCase();
                    const textGroupId = `${areaNameForText}_TEXT`;
                    const found = document.getElementById(textGroupId);
                    const textGroup = found && g.contains(found) ? found : null;
                    let centerX, centerY;
                    if (textGroup) {
                        // Get the text group bounding box
                        const textBBox = textGroup.getBBox();
                        // Check for rotation on text group, parent, or sibling rect
                        let rotation = 0;
                        // Check text group transform
                        let transform = textGroup.getAttribute("transform");
                        // If not found, check parent (the area g) transform
                        if (!transform || !transform.includes("rotate")) {
                            transform = g.getAttribute("transform");
                        }
                        // If still not found, check for rect sibling with transform
                        if (!transform || !transform.includes("rotate")) {
                            const rect = g.querySelector("rect[transform]");
                            if (rect) {
                                transform = rect.getAttribute("transform");
                            }
                        }
                        // Extract rotation angle from transform
                        if (transform) {
                            const rotateMatch = transform.match(/rotate\(([^)]+)\)/);
                            if (rotateMatch) {
                                const params = rotateMatch[1].split(/[\s,]+/);
                                rotation = parseFloat(params[0]);
                            }
                        }
                        // Normalize rotation to 0-360 range
                        rotation = ((rotation % 360) + 360) % 360;
                        // Fixed icon size at 24px
                        const iconRadius = 12; // 24px / 2
                        const spacing = 8;
                        if (rotation >= 45 && rotation < 135) {
                            // Vertical text (90° clockwise) - position above
                            centerX = textBBox.x + textBBox.width / 2;
                            centerY = textBBox.y - spacing - iconRadius;
                        }
                        else if (rotation >= 135 && rotation < 225) {
                            // Upside down text (180°) - position to the right
                            centerX = textBBox.x + textBBox.width + spacing + iconRadius;
                            centerY = textBBox.y + textBBox.height / 2;
                        }
                        else if (rotation >= 225 && rotation < 315) {
                            // Vertical text (270° / 90° counter-clockwise) - position below
                            centerX = textBBox.x + textBBox.width / 2;
                            centerY = textBBox.y + textBBox.height + spacing + iconRadius;
                        }
                        else {
                            // Horizontal text (0° or close to it) - position to the left
                            centerX = textBBox.x - spacing - iconRadius;
                            centerY = textBBox.y + textBBox.height / 2;
                        }
                        const margin = 5;
                        centerX = Math.max(bbox.x + iconRadius + margin, Math.min(centerX, bbox.x + bbox.width - iconRadius - margin));
                        centerY = Math.max(bbox.y + iconRadius + margin, Math.min(centerY, bbox.y + bbox.height - iconRadius - margin));
                    }
                    else {
                        // Fixed icon size at 24px
                        const iconRadius = 12; // 24px / 2
                        centerX = bbox.x + bbox.width / 2;
                        centerY = bbox.y + bbox.height / 2;
                        const margin = 5;
                        centerX = Math.max(bbox.x + iconRadius + margin, Math.min(centerX, bbox.x + bbox.width - iconRadius - margin));
                        centerY = Math.max(bbox.y + iconRadius + margin, Math.min(centerY, bbox.y + bbox.height - iconRadius - margin));
                    }
                    const lockGroup = createLockIcon(bbox, centerX, centerY);
                    g.appendChild(lockGroup);
                }
            }
            else {
                g.style.cursor = "not-allowed";
                g.style.opacity = "0.25";
            }
        });
        svgElement.addEventListener("click", handleClick);
        return () => {
            svgElement.removeEventListener("click", handleClick);
        };
    }, [
        svgText,
        containerRef,
        sectionImages,
        areaNames,
        minLadiesRequired,
        femaleCount,
        lowestPriceAreaName,
        onAreaClick,
        onInsufficientLadiesClick,
    ]);
};
